import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import {
  FlaskConical, MessageSquare, FileText, Database, ArrowRight,
  Sparkles, Beaker, Globe, Brain, Shield
} from "lucide-react";

export default function LandingPage() {
  const { user } = useAuth();
  const features = [
    { icon: FlaskConical, title: "Formulation Prediction", desc: "Target → phr. Auto-fill from PDF/Excel/image. Chat with your formulation. Export to PDF." },
    { icon: MessageSquare, title: "Grounded Chat", desc: "Strict / Hybrid / General modes. Multi-line input. File attachments. Copy + cite sources." },
    { icon: FileText, title: "Advanced Documents", desc: "Upload PDF / Excel. Auto-summary. Search, tag, analyze. Indexed into your RAG." },
    { icon: Brain, title: "Train with Your Data", desc: "Upload any file → indexed into your private knowledge base. Q&A pairs supported." },
    { icon: Globe, title: "Multi-language", desc: "Ask in your language — answers come back in your language, polymer terms preserved." },
  ];

  const elastomerTypes = ["Natural Rubber (NR)", "Nitrile (NBR)", "EPDM", "Silicone", "Neoprene (CR)", "FKM/Viton", "Butyl (IIR)", "Polyurethane"];

  return (
    <div className="min-h-screen bg-slate-50">
      <nav className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center"><Beaker className="w-5 h-5 text-white" /></div>
            <span className="font-heading font-bold text-xl text-slate-900">ElastoAI</span>
          </Link>
          <div className="flex items-center gap-3">
            {user ? (
              <Link to="/dashboard"><Button data-testid="dashboard-nav-btn" className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-6">Dashboard <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
            ) : (
              <>
                <Link to="/login"><Button data-testid="login-nav-btn" variant="ghost" className="text-slate-600 hover:text-slate-900">Sign In</Button></Link>
                <Link to="/register"><Button data-testid="register-nav-btn" className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-6">Get Started</Button></Link>
              </>
            )}
          </div>
        </div>
      </nav>

      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" /> AI for Polymers & Elastomers
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-tight mb-6">
              Predict Elastomer Formulations<br />
              with <span className="text-orange-500">Scientific Precision</span>
            </h1>
            <p className="text-lg text-slate-600 mb-8 max-w-xl">
              Grounded RAG · Strict polymer-only guardrails · File-aware chat · Auto-extract from PDF & Excel · Export to PDF.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to={user ? "/dashboard" : "/register"}>
                <Button data-testid="hero-cta-btn" className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-8 h-12">Start Predicting <ArrowRight className="w-5 h-5 ml-2" /></Button>
              </Link>
              <Link to={user ? "/chat" : "/login"}>
                <Button data-testid="hero-chat-btn" variant="outline" className="border-slate-300 rounded-full px-8 h-12">
                  <MessageSquare className="w-5 h-5 mr-2" /> Try Chat
                </Button>
              </Link>
            </div>
          </div>
          <div className="relative hidden lg:block">
            <img src="https://images.unsplash.com/photo-1707944746508-f2dd7b88ac1b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHw0fHxsYWJvcmF0b3J5JTIwc2NpZW50aXN0JTIwbWljcm9zY29wZXxlbnwwfHx8fDE3Njc4NjAyMjV8MA&ixlib=rb-4.1.0&q=85" alt="Lab" className="rounded-2xl shadow-2xl" />
            <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-4 border border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center"><FlaskConical className="w-6 h-6 text-green-600" /></div>
                <div>
                  <p className="font-mono text-xs text-slate-500">Predicted Hardness</p>
                  <p className="font-heading font-bold text-slate-900">65 ± 3 Shore A</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-10 bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 flex items-center gap-3 overflow-x-auto">
          <span className="text-sm font-medium text-slate-500 whitespace-nowrap">Supported:</span>
          {elastomerTypes.map((t) => <span key={t} className="px-4 py-1.5 bg-slate-100 rounded-full text-sm text-slate-700 whitespace-nowrap">{t}</span>)}
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-slate-900 mb-4">Built for serious R&D</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">Six core modules, one polymer/elastomer brain.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-2xl p-7 hover:border-orange-300 hover:-translate-y-1 transition-all">
                <div className="w-12 h-12 rounded-xl bg-slate-900 flex items-center justify-center mb-5"><f.icon className="w-6 h-6 text-white" /></div>
                <h3 className="font-heading text-lg font-bold text-slate-900 mb-2">{f.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-slate-900">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-3xl lg:text-4xl font-bold text-white mb-4">Ready to compound smarter?</h2>
          <p className="text-lg text-slate-400 mb-8">Join engineers and chemists using AI to accelerate rubber development.</p>
          <Link to={user ? "/dashboard" : "/register"}>
            <Button data-testid="cta-btn" className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-10 h-14 text-lg">Get Started Free <ArrowRight className="w-5 h-5 ml-2" /></Button>
          </Link>
        </div>
      </section>

      <footer className="bg-white border-t border-slate-200 py-8">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between text-sm text-slate-500">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-orange-500 flex items-center justify-center"><Beaker className="w-4 h-4 text-white" /></div>
            <span className="font-heading font-bold text-slate-900">ElastoAI</span>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="hover:text-slate-900">Privacy</Link>
            <Link to="/terms" className="hover:text-slate-900">Terms</Link>
            <span>© 2026 ElastoAI</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
